// The CRM tabs' last answers. Each tab read takes 2 to 3 s, so a tab you come back to shows what
// it read last time at once while a fresh read replaces it. A read already on its way is shared,
// so the shell's early read and the tab's own are one request.
// adminBillingApi (api.mjs) empties this on every write it sends, so a change made in the CRM is
// never shown undone. Answers are kept per read, per session and per request, so one admin is
// never shown another's, and two reads that send the same data never swap answers.
// Not react-query, which the app also has: each tab keeps its own effect, cancel guard and error
// and loading states, and only gains a first paint from its last answer; moving the six reads
// onto useQuery would rewrite each tab and its tests.

const KEPT = 50;
const answers = new Map(); // read -> Map(key -> answer)
const pending = new Map(); // read -> Map(key -> promise)

const shelf = (store, read) => {
  if (!store.has(read)) store.set(read, new Map());
  return store.get(read);
};

// Null without a session: nothing is kept for a caller who cannot be told apart from another.
const keyOf = (params) => {
  const session = params?.context?.sessionContext?.session?.session_id;
  return session ? JSON.stringify([session, params?.data ?? null]) : null;
};

// Calls showLast(answer) straight away when this exact read has one, and returns a fresh read.
export function readWithLastAnswer(read, params, showLast) {
  const key = keyOf(params);
  if (key === null) return read(params);
  const kept = shelf(answers, read);
  const out = shelf(pending, read);
  if (showLast && kept.has(key)) showLast(kept.get(key));
  if (out.has(key)) return out.get(key);

  const fresh = read(params).then((answer) => {
    // `kept` is the shelf this read started on. A write drops every shelf, so an answer from
    // before it lands on one nobody reads. Not kept either: an answer that says it could not
    // measure (available: false), which would only show an old failure again.
    if (answer?.available !== false) {
      kept.delete(key);
      kept.set(key, answer);
      if (kept.size > KEPT) kept.delete(kept.keys().next().value);
    }
    return answer;
  });
  out.set(key, fresh);
  const settled = () => {
    if (out.get(key) === fresh) out.delete(key);
  };
  fresh.then(settled, settled);
  return fresh;
}

// Starts a read for a tab that may be opened next, unless it is already kept or on its way.
export function warmRead(read, params) {
  const key = keyOf(params);
  if (key === null || answers.get(read)?.has(key) || pending.get(read)?.has(key)) return;
  readWithLastAnswer(read, params).catch(() => {});
}

export function forgetCrmReads() {
  answers.clear();
  pending.clear();
}
